import { GridSortDirection } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { setExportProgress } from 'common/components/exportDialog/exportDialogSlice';
import { NUMBER_OF_RETRIES,USER_TENANT_MEMBERSHIPS_MAX_ENTITIES_NUMBER_PER_REQUEST } from 'common/constants/exportData';
import { GlobalRole } from 'common/types/Entities';
import { getActiveStatus, getInactiveStatus } from 'common/utils/userMembershipColumnsHelper';

import { APISuiteGet } from '../ApplicationSuites/ApplicationSuites.api';
import { APISuiteGetResponse } from '../ApplicationSuites/ApplicationSuitesApiTypeDefs';
import { APITenantGetResponse } from '../Tenants/TenantApiTypeDefs';
import { APITenantGet } from '../Tenants/Tenants.api';

import {
    APITenantUserMembershipDelete,
    APITenantUserMembershipGetPaginatedList,
    APITenantUserMembershipGetSingle,
    APITenantUserMembershipPost,
    APITenantUserMembershipPut,
    APIUserTenantMembershipGet,
} from './TenantUserMembership.api';
import { _formatTenantUserMembershipResponse } from './TenantUserMembershipApiHelpers';
import {
    APITenantUserMembershipGetPaginatedListParams,
    APITenantUserMembershipGetSingleResponse,
} from './TenantUserMembershipApiTypeDefs';
import * as thunkTypes from './TenantUserMembershipThunkTypeDefs';

export const fetchTenantUserPaginatedMemberships = createAsyncThunk('tenant/fetchUsers', async (params: APITenantUserMembershipGetPaginatedListParams) => {
    const response = await APITenantUserMembershipGetPaginatedList(params);
    return {
        data: _formatTenantUserMembershipResponse(response.data),
        skipped: response.skipped,
        taken: response.taken,
        count: response.count,
        requestPayload: {
            tenantId: params.tenantId,
            licenseId: params.licenseId
        }
    };
});

const MAX_PARALLEL_REQUESTS = 2;

export const fetchAllFilteredSortedTenantUserMemberships = createAsyncThunk('tenant/fetchlFilteredSortedTenantUserMemberships', async (params: {
    tenantId: string,
    rowsCount?: number,
    search?: string,
    globalRoles?: GlobalRole[],
    status?: string[],
    sortKey?: string,
    sortOrder?: GridSortDirection,
    licenseId?: string,
    signal: AbortSignal,
    excludeDetails?: boolean,
    maxNumberOfEntitiesPerRequest?: number
}, thunkAPI) => {
    const maxNumberOfEntities = params.maxNumberOfEntitiesPerRequest ?? USER_TENANT_MEMBERSHIPS_MAX_ENTITIES_NUMBER_PER_REQUEST;
    const totalRecords = params.rowsCount;

    if (totalRecords === undefined || totalRecords === null) {
        throw new Error('Total records count is required for parallel export');
    }

    const totalBatches = Math.ceil(totalRecords / maxNumberOfEntities);
    const allResults: { batchIndex: number; data: ReturnType<typeof _formatTenantUserMembershipResponse> }[] = [];
    let completedBatches = 0;
    let loadedRecords = 0;

    thunkAPI.dispatch(setExportProgress({
        currentBatch: 0,
        totalBatches,
        loadedRecords: 0,
        totalRecords
    }));

    // Process batches with limited parallelism
    for (let i = 0; i < totalBatches; i += MAX_PARALLEL_REQUESTS) {
        if (params.signal?.aborted) {
            throw new Error('Export cancelled');
        }

        const batchChunk = Array.from(
            { length: Math.min(MAX_PARALLEL_REQUESTS, totalBatches - i) },
            (_, j) => {
                const batchIndex = i + j;
                const skip = batchIndex * maxNumberOfEntities;

                return APITenantUserMembershipGetPaginatedList({
                    ...params,
                    skip,
                    take: maxNumberOfEntities,
                    retries: NUMBER_OF_RETRIES
                }).then(response => {
                    if (params.signal?.aborted) {
                        throw new Error('Export cancelled');
                    }

                    const formattedData = _formatTenantUserMembershipResponse(response.data);
                    completedBatches++;
                    loadedRecords += formattedData.length;

                    thunkAPI.dispatch(setExportProgress({
                        currentBatch: completedBatches,
                        totalBatches,
                        loadedRecords,
                        totalRecords
                    }));

                    return { batchIndex, data: formattedData };
                });
            }
        );

        const chunkResults = await Promise.all(batchChunk);
        allResults.push(...chunkResults);
    }

    // Sort by batch index and flatten to maintain correct order
    allResults.sort((a, b) => a.batchIndex - b.batchIndex);
    return allResults.flatMap(r => r.data);
});

export const deleteTenantUserMembership = createAsyncThunk('tenants/deleteUser', async (item: {
    tenantId: string,
    userId: string,
    skip?: number,
    take?: number,
    search?: string;
    globalRoles?: GlobalRole[];
    status?: string[];
}) => {
    const request = { tenantId: item.tenantId, userId: item.userId };
    const response = await APITenantUserMembershipDelete(request);
    const gridData = await APITenantUserMembershipGetPaginatedList({
        tenantId: item.tenantId,
        skip: item.skip,
        take: item.take,
        search: item.search,
        globalRoles: item.globalRoles,
        status: item.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatTenantUserMembershipResponse(data);
    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});

export const postTenantUserMembership = createAsyncThunk('tenant/postUser', async (item: thunkTypes.PostUserTenantThunkParams) => {
    const response = await APITenantUserMembershipPost(item);
    const gridData = await APITenantUserMembershipGetPaginatedList({
        tenantId: item.tenantId,
        skip: item.paginationInfo.skip,
        take: item.paginationInfo.take,
        search: item.paginationInfo.search,
        globalRoles: item.paginationInfo.globalRoles,
        status: item.paginationInfo.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatTenantUserMembershipResponse(data);
    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});

export const putTenantUserMembership = createAsyncThunk('tenant/putUser', async (item: thunkTypes.PutUserTenantThunkParams) => {
    const response = await APITenantUserMembershipPut(item);
    const gridData = await APITenantUserMembershipGetPaginatedList({
        tenantId: item.tenantId,
        skip: item.paginationInfo.skip,
        take: item.paginationInfo.take,
        search: item.paginationInfo.search,
        globalRoles: item.paginationInfo.globalRoles,
        status: item.paginationInfo.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatTenantUserMembershipResponse(data);
    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});

export const fetchTenantUserMembershipDetails = createAsyncThunk(
    'tenants/fetchUser',
    async (item: { appSuiteId: string, tenantId: string, userId: string }): Promise<thunkTypes.FetchTenantUserMembershipDetailsResponse> => {
        const requests = [
            APISuiteGet({ id: item.appSuiteId }),
            APITenantGet({ id: item.tenantId }),
            APITenantUserMembershipGetSingle({ userId: item.userId, tenantId: item.tenantId })
        ];

        const responses = await Promise.all(requests);
        const apiAppSuiteResponse = responses[0] as APISuiteGetResponse;
        const apiTenantResponse = responses[1] as APITenantGetResponse;
        const apiUserResponse = responses[2] as APITenantUserMembershipGetSingleResponse;
        const profile = apiUserResponse?.data?.user?.profile;

        return {
            applicationSuiteId: apiAppSuiteResponse.data.id,
            applicationSuiteName: apiAppSuiteResponse.data.name,
            id: apiTenantResponse.data.id,
            name: apiTenantResponse.data.name,
            globalRoles: apiUserResponse?.data?.globalRoles?.map(elem => elem.name).join(', ') || null,
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            userId: apiUserResponse.data.userId,
            oasysId: apiUserResponse?.data?.oasysId ?? '',
            isActive: apiUserResponse.data.isActive ? getActiveStatus() : getInactiveStatus(),
            tenantRequiresLicense: apiTenantResponse.data.isLicenseRequired,
            startDate: apiUserResponse.data.startDate,
            endDate: apiUserResponse.data.endDate,
            status: apiUserResponse.data.status,
            aclCounts: apiUserResponse.data.aclCounts,
            claimCounts: apiUserResponse.data.claimCounts,
            roleCounts: apiUserResponse.data.roleCounts,
            globalRoleCounts: apiUserResponse.data.globalRoleCounts
        };
    }
);

export const fetchTenantUserMemberships = createAsyncThunk('users/fetchTenants', async (params: {
    userId: string,
    }) => {
    const response = await APIUserTenantMembershipGet(params);
    return response.data;
});
